/**
 * UTM and marketing attribution parameter tracking utilities.
 * Used to capture UTM parameters and click IDs from URL query strings for attribution.
 *
 * Params are persisted in sessionStorage to survive navigation during signup flow
 * (e.g., OAuth redirects that strip query params from the URL).
 */
import { GTM_DENIED_URL_PARAMS } from '@/lib/analytics/gtmRouteGate';

export interface UtmParams {
  // UTM parameters
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  // Google
  gclid?: string; // Google Click ID
  gclsrc?: string; // Google Click Source
  gad_source?: string; // Google Ads Source
  gbraid?: string; // Google App Campaign (iOS)
  wbraid?: string; // Google App Campaign (web-to-app)
  // Meta/Facebook
  fbclid?: string; // Facebook Click ID
  // Microsoft/Bing
  msclkid?: string; // Microsoft Click ID
  // TikTok
  ttclid?: string; // TikTok Click ID
  // LinkedIn
  li_fat_id?: string; // LinkedIn First-Party Ad Tracking
  // Twitter/X
  twclid?: string; // Twitter Click ID
  // Reddit
  rdt_cid?: string; // Reddit Click ID
  // Snapchat
  sccid?: string; // Snapchat Click ID
  // Pinterest
  epik?: string; // Pinterest Click ID
  // Instagram
  igshid?: string; // Instagram Share ID
  // Impact Radius
  irclid?: string; // Impact Radius Click ID
  // Mailchimp
  mc_cid?: string; // Mailchimp Campaign ID
  // Quora
  qclid?: string; // Quora Click ID
}

const UTM_STORAGE_KEY = 'heidi_utm_params';
const LANDING_URL_STORAGE_KEY = 'heidi_landing_url';

// Shared constants to avoid duplication and ensure consistency
const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const CLICK_ID_KEYS = [
  'gclid',
  'gclsrc',
  'gad_source',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'twclid',
  'rdt_cid',
  'sccid',
  'epik',
  'igshid',
  'irclid',
  'mc_cid',
  'qclid',
] as const;

const UTM_KEYS = [...UTM_PARAM_KEYS, ...CLICK_ID_KEYS] as const;

/**
 * Extracts UTM parameters from URL query strings.
 * Used internally - prefer getUtmParams() which also checks sessionStorage.
 */
function extractUtmParamsFromUrl(searchParams?: URLSearchParams): UtmParams {
  const params =
    searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams());

  const result: UtmParams = {};

  // Extract UTM params
  for (const key of UTM_PARAM_KEYS) {
    const value = params.get(key);
    if (value) result[key] = value;
  }

  // Extract click IDs
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key);
    if (value) result[key] = value;
  }

  return result;
}

/**
 * Saves UTM params to sessionStorage for persistence across navigation.
 */
function saveUtmParamsToStorage(params: UtmParams): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
  } catch {
    // sessionStorage not available (e.g., private browsing)
  }
}

/**
 * Validates and sanitizes parsed JSON to ensure it matches UtmParams shape.
 * Returns only valid string properties, ignoring any corrupted or unexpected data.
 */
function sanitizeUtmParams(data: unknown): UtmParams {
  if (typeof data !== 'object' || data === null) return {};

  const result: UtmParams = {};
  const obj = data as Record<string, unknown>;

  for (const key of UTM_KEYS) {
    const value = obj[key];
    if (typeof value === 'string' && value) {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Retrieves UTM params from sessionStorage.
 */
function getUtmParamsFromStorage(): UtmParams {
  if (typeof window === 'undefined') return {};
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      return sanitizeUtmParams(parsed);
    }
  } catch {
    // sessionStorage not available or invalid JSON
  }
  return {};
}

/**
 * Gets UTM and marketing attribution parameters.
 * First checks URL for fresh params (and saves them), then falls back to sessionStorage.
 *
 * @param searchParams - Optional URLSearchParams for testing
 * @returns Object containing any found UTM parameters
 *
 * @example
 * // User lands on: https://app.heidihealth.com/register?utm_source=google&utm_medium=cpc
 * // Later during signup (URL no longer has params):
 * const params = getUtmParams();
 * // Returns: { utm_source: 'google', utm_medium: 'cpc' } (from sessionStorage)
 */
export function getUtmParams(searchParams?: URLSearchParams): UtmParams {
  // First, check URL for fresh UTM params
  const urlParams = extractUtmParamsFromUrl(searchParams);

  // If we found params in URL, save them and return
  if (Object.keys(urlParams).length > 0) {
    saveUtmParamsToStorage(urlParams);
    return urlParams;
  }

  // Otherwise, return any previously stored params
  return getUtmParamsFromStorage();
}

/**
 * Captures UTM params and the full landing URL from the current URL.
 * Call this early in app initialization to ensure params are captured before any navigation.
 *
 * The landing URL is only saved if it contains attribution params (UTM or click IDs).
 */
export function captureUtmParams(): void {
  if (typeof window === 'undefined') return;
  const urlParams = extractUtmParamsFromUrl();
  if (Object.keys(urlParams).length > 0) {
    saveUtmParamsToStorage(urlParams);
    // Also save the full landing URL if we haven't already. The landing URL is
    // replayed into the signup conversion (GTM dataLayer, Segment → Meta CAPI),
    // so a clinical question riding on it as `?query=` must never be stored
    // (ARCFE-1182). Same denied-param list as the GTM gate.
    try {
      if (!sessionStorage.getItem(LANDING_URL_STORAGE_KEY)) {
        sessionStorage.setItem(
          LANDING_URL_STORAGE_KEY,
          redactDeniedUrlParams(window.location.href)
        );
      }
    } catch {
      // sessionStorage not available (e.g., private browsing, restricted context)
    }
  }
}

/**
 * Drop the GTM-denied query params from a URL at the string level, so every
 * surviving param stays byte-identical (a `URLSearchParams` round trip would
 * re-encode retained attribution values, e.g. `%20` → `+`).
 */
function redactDeniedUrlParams(href: string): string {
  const hashAt = href.indexOf('#');
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  const beforeHash = hashAt === -1 ? href : href.slice(0, hashAt);
  const qAt = beforeHash.indexOf('?');
  if (qAt === -1) return href;
  const base = beforeHash.slice(0, qAt);
  const denied = new Set<string>(GTM_DENIED_URL_PARAMS);
  const isDenied = (pair: string): boolean => {
    const rawKey = pair.split('=')[0] ?? '';
    try {
      return denied.has(decodeURIComponent(rawKey.replace(/\+/g, ' ')));
    } catch {
      return denied.has(rawKey);
    }
  };
  const kept = beforeHash
    .slice(qAt + 1)
    .split('&')
    .filter((pair) => pair.length > 0 && !isDenied(pair));
  return `${base}${kept.length ? `?${kept.join('&')}` : ''}${hash}`;
}

/**
 * Gets the original landing URL that contained attribution params.
 * Returns null if no landing URL was captured.
 *
 * @example
 * // User landed on: https://app.heidihealth.com/demo?utm_source=google&gclid=abc123
 * // After OAuth redirect, URL is: https://app.heidihealth.com/callback
 * const url = getLandingUrl();
 * // Returns: "https://app.heidihealth.com/demo?utm_source=google&gclid=abc123"
 */
export function getLandingUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(LANDING_URL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * @deprecated Use getUtmParams() instead which persists params in sessionStorage
 */
export function getUtmParamsFromUrl(searchParams?: URLSearchParams): UtmParams {
  return getUtmParams(searchParams);
}

/**
 * Seeds UTM params from an external source (e.g., a postMessage from a parent window).
 * Only saves if the provided params contain at least one valid UTM key.
 * Existing params in sessionStorage are NOT overwritten if they already exist —
 * URL-captured params take priority. Call this before any getUtmParams() calls.
 */
export function setUtmParamsFromExternal(params: Record<string, unknown>): void {
  const sanitized = sanitizeUtmParams(params);
  if (Object.keys(sanitized).length === 0) return;
  // Only seed if sessionStorage doesn't already have params (URL-captured params win)
  const existing = getUtmParamsFromStorage();
  if (Object.keys(existing).length === 0) {
    saveUtmParamsToStorage(sanitized);
  }
}

const META_BROWSER_ID_COOKIE = '_fbp';
const META_CLICK_ID_COOKIE = '_fbc';

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  const value = match?.[1];
  return value === undefined ? undefined : decodeURIComponent(value);
}

/**
 * Meta click id (`fbc`) in Meta's required `fb.1.{ms}.{fbclid}` format. Prefers
 * the Pixel-set `_fbc` cookie (authoritative click timestamp); falls back to
 * building from the first-touch `fbclid` (persisted in sessionStorage so it
 * survives the OAuth redirect that strips the URL). Returns undefined when
 * neither is available.
 */
function getMetaClickId(fbclid: string | undefined): string | undefined {
  const clickIdCookie = readCookie(META_CLICK_ID_COOKIE);
  if (clickIdCookie) return clickIdCookie;

  if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;

  return undefined;
}

/**
 * First-party ad-platform attribution ids attached to the Segment user profile
 * so the Meta / Google Ads CAPI insert functions can hydrate their conversion
 * `user_data` (Meta `fbc`/`fbp`; Google `gclid`/`gbraid`/`wbraid`) for
 * server-side events that fire days after the browser session ends.
 *
 * These are browser-only signals, so they must ride on the frontend identify to
 * reach the unified profile. They use dedicated `attribution_*` keys that no
 * other Segment source or Unify trait writes, so nothing external can overwrite
 * them (unlike `page_url` / `useragent`, which are maintained elsewhere).
 *
 * Semantics are last-touch, not first-touch: a later identify with a present
 * signal (a fresh ad click, or the still-set `_fbc`/`_fbp` cookie) overwrites
 * the stored value. Omit-when-absent only stops a signal-less session from
 * blanking an existing value. Google click ids are raw (no formatting, unlike
 * Meta's `fbc`).
 */
export interface AttributionIdentifyTraits {
  attribution_fbc?: string;
  attribution_fbp?: string;
  attribution_gclid?: string;
  attribution_gbraid?: string;
  attribution_wbraid?: string;
}

/**
 * Collects the browser-only ad-platform attribution ids for the Segment identify
 * call. Keys are omitted when absent so a signal-less session never blanks an
 * existing profile value; when a signal is present it is sent (last-touch).
 */
export function getAttributionIdentifyTraits(): AttributionIdentifyTraits {
  if (typeof window === 'undefined') return {};

  const traits: AttributionIdentifyTraits = {};
  const utmParams = getUtmParams();

  const metaClickId = getMetaClickId(utmParams.fbclid);
  if (metaClickId) traits.attribution_fbc = metaClickId;

  const metaBrowserId = readCookie(META_BROWSER_ID_COOKIE);
  if (metaBrowserId) traits.attribution_fbp = metaBrowserId;

  if (utmParams.gclid) traits.attribution_gclid = utmParams.gclid;
  if (utmParams.gbraid) traits.attribution_gbraid = utmParams.gbraid;
  if (utmParams.wbraid) traits.attribution_wbraid = utmParams.wbraid;

  return traits;
}

/**
 * Meta Pixel cookie ids for ad matching, under plain key names a GTM tag /
 * conversion event can consume directly: `fbp` (browser id, `_fbp` cookie) and
 * `fbc` (click id in Meta's `fb.1.{ms}.{fbclid}` format — prefers the `_fbc`
 * cookie, falls back to building from the (persisted) `fbclid`). Unlike
 * `getAttributionIdentifyTraits()` (which prefixes `attribution_*` for the
 * Segment identify), these keep the raw `fbp`/`fbc` names. Keys omitted when
 * absent. Browser-only.
 *
 * @param fbclid Optional first-touch Facebook click id (from `getUtmParams()`),
 *   used to synthesise `fbc` when the `_fbc` cookie is not present.
 */
export function getMetaCookieIds(fbclid?: string): { fbp?: string; fbc?: string } {
  if (typeof window === 'undefined') return {};

  const result: { fbp?: string; fbc?: string } = {};

  const fbp = readCookie(META_BROWSER_ID_COOKIE);
  if (fbp) result.fbp = fbp;

  const fbc = getMetaClickId(fbclid);
  if (fbc) result.fbc = fbc;

  return result;
}
